import { Fragment, type CSSProperties } from 'react';
import { useClientTranslation } from '@/lib/core/i18n';
import { Button } from '@/components/ui/button';
import { Icon } from '@/components/ui/icon';
import { Drawer, DrawerContent, DrawerSwipeArea } from '@/components/ui/drawer';
import type { VenueGroupDto, VenueGroupVenueDto } from '@/services/platform/api/model';
import { AssistanceBlock } from '../ui/assistance-block';
import { EditorialTitle } from '../ui/editorial-title';
import { EyebrowTags } from '../ui/eyebrow-tags';
import { VenueCard } from './venue-card';

const SLUG_EVENT = 'event';
const SLUG_EXPERIENCE = 'experiences';

function VenueCountLabel({ venues }: { venues: VenueGroupDto['venues'] }) {
  const t = useClientTranslation();
  const list = venues ?? [];
  const eventCount = list.filter(v => v.serviceCategorySlug === SLUG_EVENT).length;
  const experienceCount = list.filter(v => v.serviceCategorySlug === SLUG_EXPERIENCE).length;
  const venueCount = list.length - eventCount - experienceCount;
  const parts = [
    venueCount > 0 ? t('App.Explore.Map.Venues', { count: venueCount }) : null,
    eventCount > 0 ? t('App.Explore.Map.Events', { count: eventCount }) : null,
    experienceCount > 0 ? t('App.Explore.Map.Experiences', { count: experienceCount }) : null,
  ].filter(Boolean);
  return (
    <div className="flex items-center gap-2">
      {parts.map((part, i) => (
        // The key belongs on the fragment, not on its children — React cannot pair up siblings
        // across renders otherwise, and warns about it.
        <Fragment key={i}>
          {i > 0 && <span className="border-jncquoi-neutral-500 h-3 border-l" />}
          <span className="font-pp-neue-montreal text-jncquoi-neutral-800 text-[12px] tracking-[0.5px] uppercase">
            {part}
          </span>
        </Fragment>
      ))}
    </div>
  );
}

interface VenuePeekBarProps {
  resultCount: number;
  onExpand: () => void;
}

/**
 * The 80px strip resting at the bottom of the map.
 *
 * Rendered as a `DrawerSwipeArea`, so the strip can be dragged upwards to open the sheet as well
 * as tapped — the handle looks grabbable, so it should be. Being a swipe area means it must live
 * inside `VenueSheet`'s `Drawer` root rather than beside it.
 *
 * It is not a snap point: the strip has to stay visible with the map pins clickable behind it and
 * no backdrop, which Base UI cannot express per snap point (`modal` is a root prop, so it cannot
 * be off at one snap point and on at another).
 *
 * Kept mounted while the sheet is open — it sits under the drawer (`z-40` vs `z-80`) and is
 * revealed by the closing animation instead of popping back in once the drawer has gone.
 */
export function VenuePeekBar({ resultCount, onExpand }: VenuePeekBarProps) {
  const t = useClientTranslation();
  const label = t('App.Explore.Map.ViewAllResults', { count: resultCount });

  return (
    <>
      {/*
        The screen-reader control. Base UI renders the swipe area below as
        `role="presentation" aria-hidden="true"`, which drops everything inside it — the visible
        button included — from the accessibility tree. Wrapping the strip is nonetheless what
        makes the drag work: with the swipe area behind the content instead, the button covers
        most of the strip and intercepts the pointer before the gesture is ever recognised. So the
        accessible control lives out here, and the visible one is left as a pointer target.
      */}
      <button type="button" className="sr-only" onClick={onExpand}>
        {label}
      </button>

      <DrawerSwipeArea className="bg-jncquoi-neutral-00 fixed inset-x-0 bottom-0 z-40 flex flex-col rounded-t-sm">
        {/* Decorative handle — advertises the upward drag the swipe area listens for. */}
        <div className="flex w-full shrink-0 justify-center pt-2 pb-0" aria-hidden="true">
          <div className="bg-jncquoi-neutral-1000/30 h-1.25 w-9 cursor-grab rounded-full" />
        </div>

        {/* `tabIndex={-1}`: it sits inside an `aria-hidden` subtree, so leaving it focusable would
            put a tab stop on a control no assistive tech can announce. The `sr-only` button above
            is the single accessible stop. */}
        <Button
          variant="tertiary-link"
          palette="dark"
          tabIndex={-1}
          className="text-jncquoi-neutral-800 h-auto min-w-0 shrink-0 rounded-none px-0 pt-6 pb-7.5 text-[12px] tracking-[0.5px] uppercase"
          onClick={onExpand}
        >
          {label}
        </Button>
      </DrawerSwipeArea>
    </>
  );
}

interface VenueSheetProps {
  isExpanded: boolean;
  activeGroup: VenueGroupDto | null;
  sheetVenues: VenueGroupDto[];
  currentLocation?: { name?: string | null };
  activeCategoryLabel: string;
  activeCategoryTitle: string;
  resultCount: number;
  /** Height of the map's sticky header, so the sheet stops below it rather than covering it. */
  headerHeight: number;
  isBookmarked: (venue: VenueGroupVenueDto) => boolean;
  onExpand: () => void;
  onCollapse: () => void;
  onBookmark: (venue: VenueGroupVenueDto) => void;
  onVenueTap?: (venue: VenueGroupVenueDto, groupName?: string | null) => void;
}

export function VenueSheet({
  isExpanded,
  activeGroup,
  sheetVenues,
  currentLocation,
  activeCategoryLabel,
  activeCategoryTitle,
  resultCount,
  headerHeight,
  isBookmarked,
  onExpand,
  onCollapse,
  onBookmark,
  onVenueTap,
}: VenueSheetProps) {
  return (
    <Drawer
      open={isExpanded}
      // Both branches are live: the peek bar's swipe area opens, while swipe-down, Escape and
      // an outside press all close.
      onOpenChange={open => (open ? onExpand() : onCollapse())}
      showSwipeHandle
    >
      <VenuePeekBar resultCount={resultCount} onExpand={onExpand} />

      <DrawerContent
        // The drawer's own overlay is /40; the map sheet has always dimmed to /70.
        overlayClassName="bg-jncquoi-neutral-1000/70"
        className="pb-safe-area"
        // Declared inline so it beats the drawer's own `calc(100dvh-6rem)` default, keeping the
        // location selector and category tabs visible above the sheet.
        style={{ '--drawer-content-max-height': `calc(100svh - ${headerHeight}px)` } as CSSProperties}
      >
        <div className="flex shrink-0 flex-col items-center gap-8 px-4 pt-6 pb-4">
          <EyebrowTags labels={[currentLocation?.name, activeCategoryLabel]} />

          {activeCategoryTitle && <EditorialTitle text={activeCategoryTitle} />}

          <div className="flex flex-col items-center gap-2">
            {activeGroup?.address && (
              <div className="flex items-center gap-1">
                <Icon name="map-pin" width={16} height={16} className="text-jncquoi-neutral-800 shrink-0" />
                <span className="font-pp-neue-montreal text-jncquoi-neutral-800 text-[12px] tracking-[0.5px] uppercase">
                  {activeGroup.address}
                </span>
              </div>
            )}
            <VenueCountLabel
              venues={activeGroup ? activeGroup.venues : sheetVenues.flatMap(g => g.venues ?? [])}
            />
          </div>
        </div>

        {/* `data-base-ui-swipe-ignore` so dragging over the cards scrolls instead of dismissing. */}
        <div data-base-ui-swipe-ignore className="flex flex-1 flex-col gap-4 overflow-y-auto px-4 pb-6">
          {(activeGroup
            ? (activeGroup.venues ?? []).map(venue => ({ venue, groupName: activeGroup.name }))
            : sheetVenues.flatMap(g => (g.venues ?? []).map(venue => ({ venue, groupName: g.name })))
          ).map(({ venue, groupName }) => (
            <VenueCard
              key={venue.nodeKey}
              venue={venue}
              isBookmarked={isBookmarked(venue)}
              onBookmark={onBookmark}
              onTap={v => onVenueTap?.(v, groupName)}
            />
          ))}
          <AssistanceBlock />
        </div>
      </DrawerContent>
    </Drawer>
  );
}
